import { SIXTEENTHS } from "../movement/step.ts";
import { MAX_U16 } from "./limits.ts";
import { MAX_RESPAWN_POINT_NAME_CHARS } from "./respawn.ts";

/**
 * The `mapPlaces` message's bounds and shape (ADR-100 decision 6, PROTOCOL.md section 6): text, server to client, the
 * map's Spawn Point and the ground point and name of every shrine the player's own character has discovered on it,
 * at every join and each discovery. Its field table is in `text.ts`, which `encodeText` and `parseTextFrame` apply:
 * positions in tiles, each a whole number of sixteenths; each place may carry `art` (PROTOCOL.md 6.67.0); places strictly ascending by handle, then x, then y.
 */

/**
 * The most places one message carries: a map holds at most 64 shrines (ADR-096 decision 14).
 */
export const MAX_MAP_PLACES = 64;

/**
 * The longest name a place carries, in UTF-16 units, the respawn offer's.
 */
export const MAX_MAP_PLACE_NAME_CHARS = MAX_RESPAWN_POINT_NAME_CHARS;

/**
 * The largest coordinate a place carries, in tiles: the largest ground point a `u16` of sixteenths holds.
 */
export const MAX_MAP_PLACE_TILES = MAX_U16 / SIXTEENTHS;

/**
 * A point on the map in tiles, each coordinate a whole number of sixteenths from 0 to `MAX_MAP_PLACE_TILES`.
 */
export interface MapPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * The largest appearance index a place's art carries: 65535 is the entity table's own "none", so an index is 0 to 65534.
 */
export const MAX_MAP_ART_APPEARANCE = MAX_U16 - 1;

/**
 * What draws a discovered shrine on the map (ADR-100 decision 58): the shrine thing's appearance index and its feet
 * point in tiles, the two numbers a landmark holds.
 */
export interface MapPlaceArt extends MapPoint {
  readonly appearance: number;
}

/**
 * One known place: the shrine thing's handle (`NO_HANDLE` for a marker shrine), its ground point and its name, empty
 * for a shrine with none, and, for a shrine thing, the `art` that draws it; a marker shrine has none. A waypoint
 * (ADR-137 decision 2) carries `kind` `waypoint`; a shrine carries no `kind` (PROTOCOL.md 6.86.0).
 */
export interface MapPlace extends MapPoint {
  readonly handle: number;
  readonly name: string;
  readonly kind?: "waypoint";
  readonly art?: MapPlaceArt;
}

/**
 * The fields of a `mapPlaces` message beyond `v` and `type`.
 */
export interface MapPlaces {
  readonly spawn: MapPoint;
  readonly places: readonly MapPlace[];
}
